<template>
	<form
		class="main-wrapper mt-4 mb-3"
		@submit.prevent="handleSubmit"
	>
		<CRow>
			<!-- Page level -->
			<CCol lg="12">
				<h4 class="mb-4">
					Static page level
				</h4>
				<p class="label level-label">
					Creating static page
				</p>
				<BaseButtonGroup
					v-model="level"
					:list="PAGE_LEVEL_OPTIONS"
					:disabled="isEditMode"
					@input="handleSelectedLevel"
				/>

				<CRow
					v-if="isParentSelectable"
					class="row-parent-page"
				>
					<CCol md="6" class="mb-3">
						<BaseDropDown
							v-model="$v.pageParentLevelOne.$model"
							:options="pageParentLevelOneOptions"
							:searchable="false"
							:is-valid="!$v.pageParentLevelOne.$error"
							:invalid-feedback="$t('global.error.required')"
							label-drop-down="Under parent page (level 1)*"
							label="name"
							track-by="value"
							placeholder="Selection"
							class="select-custom"
							test-id="page-level-one-dropdown"
						/>
					</CCol>
				</CRow>
				<hr>
			</CCol>
			<!-- end Page level -->
			<!-- General info -->
			<CCol lg="12">
				<h4 class="mb-4">
					General info
				</h4>
				<CRow>
					<CCol md="6" class="mt-2">
						<CInput
							v-model.trim="$v.nameTH.$model"
							:is-valid="!$v.nameTH.$error && null"
							:invalid-feedback="$t('global.error.required')"
							label="Static page name (TH)*"
							@blur="handleAutoFillMetaTitleTH"
						/>
					</CCol>
					<CCol md="6" class="mt-2">
						<CInput
							v-model.trim="$v.name.$model"
							:is-valid="!$v.name.$error && null"
							:invalid-feedback="$t('global.error.required')"
							label="Static page name (EN)*"
							@blur="handleAutoFillMetaTitleAndSlug"
						/>
					</CCol>
					<CCol md="12" class="mt-2">
						<CInput
							v-model.trim="$v.customUrl.$model"
							:is-valid="!$v.customUrl.$error && null"
							:invalid-feedback="$t('global.error.url')"
							:description="'รองรับเฉพาะ URL เท่านั้น ตัวอย่างเช่น: https://www.studio7thailand.com'"
							label="Custom URL"
						/>
					</CCol>
					<CCol md="12" class="mt-2 text-muted">
						<CInput
							v-model.trim="$v.slug.$model"
							:is-valid="isValidSlug"
							:invalid-feedback="validateSlugMessage"
							:description="slugHelperText"
							label="Slug"
							placeholder="slug-url"
							@input="handleClearErrorSlug"
						/>
					</CCol>
					<CCol md="12" class="mt-3">
						<div class="d-flex align-items-center">
							<label class="toggle-label mb-0" for="page-status">Status</label>
							<CSwitch
								id="page-status"
								:checked="status"
								variant="3d"
								size="sm"
								class="switch-custom"
								color="success"
								@update:checked="handleUpdateStatus"
							/>
							<span class="ml-2">{{ statusText }}</span>
						</div>
						<small class="form-text text-muted">เปิด = หน้านี้พร้อมใช้งาน (Active) / ปิด = ปิดการใช้งานหน้า (Inactive)</small>
					</CCol>
					<CCol md="12" class="mt-3">
						<div class="d-flex align-items-center">
							<label class="toggle-label mb-0" for="page-visibility">Store-front visibility</label>
							<CSwitch
								id="page-visibility"
								:checked="visibility"
								:disabled="!status"
								variant="3d"
								size="sm"
								class="switch-custom"
								color="success"
								@update:checked="handleUpdateVisibility"
							/>
							<span class="ml-2">{{ getVisibilityText(visibility) }}</span>
						</div>
						<small class="form-text text-muted">เปิด = แสดงหน้านี้บนหน้าร้าน (storefront) ให้ลูกค้าเห็น / ปิด = ซ่อนหน้าไม่ให้แสดงบนเว็บไซต์ (ต้องเปิด Status ก่อนถึงจะตั้งค่าได้)</small>
					</CCol>
					<template>
						<CCol md="12" class="mt-3">
							<div class="d-flex align-items-center">
								<label class="toggle-label mb-0" for="header-visibility">Header visibility</label>
								<CSwitch
									id="header-visibility"
									:checked="headerVisibility"
									variant="3d"
									size="sm"
									class="switch-custom"
									color="success"
									@update:checked="handleUpdateHeaderVisibility"
								/>
								<span class="ml-2">{{ getVisibilityText(headerVisibility) }}</span>
							</div>
							<small class="form-text text-muted">เปิด = แสดงแถบเมนูด้านบน (header) ของเว็บไซต์บนหน้านี้ / ปิด = ซ่อน header</small>
						</CCol>
						<CCol md="12" class="mt-3">
							<div class="d-flex align-items-center">
								<label class="toggle-label mb-0" for="sidebar-visibility">Sidebar visibility</label>
								<CSwitch
									id="sidebar-visibility"
									:checked="sidebarVisibility"
									variant="3d"
									size="sm"
									class="switch-custom"
									color="success"
									@update:checked="handleUpdateSidebarVisibility"
								/>
								<span class="ml-2">{{ getVisibilityText(sidebarVisibility) }}</span>
							</div>
							<small class="form-text text-muted">เปิด = แสดงเมนูด้านข้าง (sidebar) บนหน้านี้ / ปิด = ซ่อน sidebar</small>
						</CCol>
						<CCol md="12" class="mt-3">
							<div class="d-flex align-items-center">
								<label class="toggle-label mb-0" for="container">Container</label>
								<CSwitch
									id="container"
									:checked="container"
									:disabled="sidebarVisibility"
									variant="3d"
									size="sm"
									class="switch-custom"
									color="success"
									@update:checked="handleUpdateContainer"
								/>
								<span class="ml-2">{{ getVisibilityText(container) }}</span>
							</div>
							<small class="form-text text-muted">เปิด = ให้ frontend ใส่ class "container" ครอบเนื้อหา (จำกัดความกว้าง จัดกึ่งกลาง) / ปิด = ไม่ใส่ container แสดงเต็มความกว้าง (full width) — เมื่อเปิด Sidebar visibility ระบบจะบังคับเป็นเปิดและแก้ไม่ได้</small>
						</CCol>
					</template>
					<CCol v-if="container" md="12" class="mt-2">
						<label class="d-block mb-2">ปิด padding ของ container (เลือกด้าน)</label>
						<div class="d-flex flex-wrap">
							<CInputCheckbox
								:checked="!containerPadding.top"
								label="บน"
								custom
								class="mr-4"
								@update:checked="handleTogglePaddingSide('top', $event)"
							/>
							<CInputCheckbox
								:checked="!containerPadding.right"
								label="ขวา"
								custom
								class="mr-4"
								@update:checked="handleTogglePaddingSide('right', $event)"
							/>
							<CInputCheckbox
								:checked="!containerPadding.bottom"
								label="ล่าง"
								custom
								class="mr-4"
								@update:checked="handleTogglePaddingSide('bottom', $event)"
							/>
							<CInputCheckbox
								:checked="!containerPadding.left"
								label="ซ้าย"
								custom
								class="mr-4"
								@update:checked="handleTogglePaddingSide('left', $event)"
							/>
						</div>
						<small class="form-text text-muted">ติ๊ก = ปิด padding ด้านนั้นของ container ให้เนื้อหาชนขอบ (มีผลเฉพาะตอน Container เปิด)</small>
					</CCol>
					<CCol md="12" class="mt-4">
						<StaticPageLayoutPreview
							:header-visibility="headerVisibility"
							:sidebar-visibility="sidebarVisibility"
							:container="container"
							:container-padding="containerPadding"
						/>
					</CCol>
				</CRow>
				<hr>
			</CCol>
			<!-- end General info -->
			<!-- SEO -->
			<CCol lg="12">
				<h4 class="mb-4">
					SEO
				</h4>
				<CCol md="12" class="p-0">
					<label class="d-block">Meta title</label>
					<CRow>
						<CCol md="6">
							<CInput
								v-model.trim="metaTitleTH"
								label="TH"
								placeholder="Meta title (TH)"
							/>
						</CCol>
						<CCol md="6">
							<CInput
								v-model.trim="metaTitle"
								label="EN"
								placeholder="Meta title (EN)"
							/>
						</CCol>
					</CRow>
				</CCol>
				<CCol md="12" class="mt-2 p-0">
					<label class="d-block">Meta keyword</label>
					<CRow>
						<CCol md="6">
							<CTextarea
								v-model.trim="metaKeywordTH"
								label="TH"
								placeholder="Meta keyword (TH)"
								rows="3"
							/>
						</CCol>
						<CCol md="6">
							<CTextarea
								v-model.trim="metaKeyword"
								label="EN"
								placeholder="Meta keyword (EN)"
								rows="3"
							/>
						</CCol>
					</CRow>
				</CCol>
				<CCol md="12" class="mt-2 p-0">
					<label class="d-block">Meta description</label>
					<CRow>
						<CCol md="6">
							<CTextarea
								v-model.trim="metaDescriptionTH"
								label="TH"
								placeholder="Meta description (TH)"
								rows="4"
								class="mb-4"
							/>
						</CCol>
						<CCol md="6">
							<CTextarea
								v-model.trim="metaDescription"
								label="EN"
								placeholder="Meta description (EN)"
								rows="4"
								class="mb-4"
							/>
						</CCol>
					</CRow>
				</CCol>
				<BaseMediaSelectorBox
					v-model="metaImage"
					title="Meta image"
					no-image-text="No meta image"
				/>
				<hr>
			</CCol>
			<!-- end SEO -->
			<!-- Content -->
			<CCol lg="12">
				<div class="d-flex justify-content-between mb-3">
					<h4>Contents</h4>
					<div class="add-widgets-container">
						<CButton
							color="secondary"
							:disabled="!isEditMode"
							@click="handlePreview"
						>
							<CIcon class="mr-2" name="cil-magnifying-glass" />Preview content
						</CButton>
					</div>
				</div>
				<div v-if="level === PAGE_LEVEL.TWO" class="d-flex alert alert-warning mb-3" role="alert">
					<CIcon class="icon-warning mr-2" name="cil-warning" />
					<p class="typo-body-2 mb-0">
						ตัว content ส่วนนี้จะใช้งานได้กับ static widget ทั้งหมด และ โดย content ส่วนนี้จะแสดงด้านบนสุดของ page เสมอ
					</p>
				</div>
				<CRow class="mt-4">
					<CCol lg="12">
						<label>TH</label>
						<BaseRichTextEditor
							v-model="contentTH"
							:is-valid="!$v.contentTH.$error"
							:invalid-feedback="contentErrors($v.contentTH)"
							name="contentTH"
							size="large"
						/>
					</CCol>
				</CRow>
				<CRow class="mt-2">
					<CCol lg="12">
						<label>EN</label>
						<BaseRichTextEditor
							v-model="content"
							:is-valid="!$v.content.$error"
							:invalid-feedback="contentErrors($v.content)"
							name="content"
							size="large"
						/>
					</CCol>
				</CRow>
				<hr class="mt-4 mb-5">
			</CCol>
			<!-- end Content -->
			<!-- Widget list -->
			<CCol v-if="level === PAGE_LEVEL.TWO" col="12" class="pb-5">
				<WidgetPageList
					:is-edit-mode="isEditMode"
					:slug="slug"
				/>
			</CCol>
			<!-- end Widget list -->
			<div class="navigation-panel">
				<div class="main-wrapper">
					<CRow>
						<CCol
							lg="12"
							class="d-flex justify-content-end align-items-center"
						>
							<a
								v-if="isEditMode"
								href="#"
								class="btn-remove mr-auto"
								@click.prevent="handleRemove"
							>
								Remove page
							</a>
							<CButton
								color="tertiary"
								class="transparent mr-3"
								@click.prevent="$router.push({ name: 'Pages' })"
							>
								Cancel
							</CButton>
							<CButton
								:disabled="isLoading"
								type="submit"
								class="transparent"
								color="primary"
							>
								{{ isEditMode ? 'Save changes' : 'Create' }}
							</CButton>
						</CCol>
					</CRow>
				</div>
			</div>
		</CRow>
	</form>
</template>

<script>
import { required, requiredIf, helpers, url as urlValidator } from 'vuelidate/lib/validators';
import BaseMediaSelectorBox from '@/components/BaseMediaSelectorBox.vue';
import WidgetPageList from '@/components/WidgetsStaticPage/WidgetPageList.vue';
import StaticPageLayoutPreview from '@/components/StaticPageLayoutPreview.vue';
import { PAGE_LEVEL, PAGE_LEVEL_OPTIONS } from '../enums/pages';
import { pathOr, scrollToTop } from '../assets/js/helpers';
import { jsonLdScriptOnly } from '../assets/js/validators';
import { transformedPageDropdownOption } from '../assets/js/transform/pages';
import slug from '../regex/slug';
import {
	STATUS as mappingStatus,
	VISIBILITY as mappingVisibility,
} from '../mapping/pages';

const slugify = require('slugify');

export default {
	name: 'FormPages',
	components: {
		BaseMediaSelectorBox,
		WidgetPageList,
		StaticPageLayoutPreview,
	},
	validations() {
		return {
			pageParentLevelOne: {
				required: (value) => {
					if (this.isParentSelectable) {
						return required(value);
					}
					return true;
				},
			},
			name: {
				required,
			},
			nameTH: {
				required,
			},
			content: {
				required: requiredIf((vm) => !vm.isLevelOne),
				jsonLdScriptOnly,
			},
			contentTH: {
				required: requiredIf((vm) => !vm.isLevelOne),
				jsonLdScriptOnly,
			},
			slug: {
				required,
				format: helpers.regex('slug', slug),
				duplicate: () => !this.isDuplicateSlug,
			},
			customUrl: {
				urlValidator,
			},
		};
	},
	props: {
		data: {
			type: Object,
			default: () => ({}),
		},
		pageList: {
			type: Array,
			default: () => [],
		},
		isLoading: {
			type: Boolean,
			default: false,
		},
		isDuplicateSlug: {
			type: Boolean,
			default: false,
		},
		isEditMode: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			level: PAGE_LEVEL.ONE,
			column: null,
			PAGE_LEVEL,
			PAGE_LEVEL_OPTIONS,
			pageParentLevelOne: null,
			name: '',
			nameTH: '',
			customUrl: null,
			slug: '',
			status: true,
			visibility: false,
			headerVisibility: false,
			sidebarVisibility: false,
			container: false,
			containerPadding: {
				top: true,
				right: true,
				bottom: true,
				left: true,
			},
			content: '',
			contentTH: '',
			metaTitle: '',
			metaTitleTH: '',
			metaKeyword: '',
			metaKeywordTH: '',
			metaDescription: '',
			metaDescriptionTH: '',
			metaImage: null,
		};
	},
	computed: {
		// Pages dropdown options
		pageParentLevelOneOptions() {
			return this.pageList.map(transformedPageDropdownOption);
		},
		isParentSelectable() {
			return this.level !== PAGE_LEVEL.ONE;
		},
		pageParentId() {
			if (this.isParentSelectable) {
				return pathOr(null, ['pageParentLevelOne', 'value'])(this);
			}
			return null;
		},
		isValidSlug() {
			const isError = pathOr(false, ['$v', 'slug', '$error'])(this);
			return isError ? false : null;
		},
		validateSlugMessage() {
			const validateRequired = pathOr(false, ['$v', 'slug', 'required'])(this);
			const validateFormat = pathOr(false, ['$v', 'slug', 'format'])(this);
			const validateDuplicate = pathOr(false, ['$v', 'slug', 'duplicate'])(this);

			if (!validateRequired) {
				return this.$t('global.error.required');
			} else if (!validateFormat) {
				return this.$t('global.slug.slugFormat');
			} else if (!validateDuplicate) {
				return this.$t('global.slug.duplicateSlug');
			}
			return null;
		},
		slugHelperText() {
			return this.isValidSlug !== false ? this.$t('global.slug.slugFormat') : '';
		},
		statusText() {
			const text = mappingStatus[this.status] || '';
			return this.$t(text);
		},
		isLevelOne() {
			return this.level === PAGE_LEVEL.ONE;
		},
	},
	mounted() {
		if (this.isEditMode) {
			this.level = this.data.level || PAGE_LEVEL.ONE;
			this.name = this.data.name;
			this.nameTH = this.data.nameTH;
			this.customUrl = this.data.customUrl;
			this.slug = this.data.slug;
			this.status = this.data.status;
			this.visibility = this.data.visibility ?? true;
			this.headerVisibility = this.data.headerVisibility ?? false;
			this.sidebarVisibility = this.data.sidebarVisibility ?? false;
			this.container = this.sidebarVisibility ? true : (this.data.container ?? false);
			if (this.data.containerPadding) {
				this.containerPadding = { ...this.containerPadding, ...this.data.containerPadding };
			}
			this.pageParentLevelOne = this.pageParentLevelOneOptions.find((option) => option.value === this.data.parentLevelOneId);
			this.content = this.data.content;
			this.contentTH = this.data.contentTH;
			this.metaTitle = this.data.metaTitle;
			this.metaTitleTH = this.data.metaTitleTH;
			this.metaKeyword = this.data.metaKeyword;
			this.metaKeywordTH = this.data.metaKeywordTH;
			this.metaDescription = this.data.metaDescription;
			this.metaDescriptionTH = this.data.metaDescriptionTH;
			this.metaImage = this.data.metaImage;
		}
	},
	methods: {
		/**
		 * ข้อความ error ของช่อง content — แยกสองสาเหตุให้ชัด
		 *
		 * ถ้าไม่แยก คนกรอกจะเห็นแค่ "Required field" ทั้งที่ปัญหาจริงคือ JSON-LD ที่วางไว้พัง
		 * แล้วไล่หาไม่เจอ เพราะช่องนี้เป็น WYSIWYG ต้องเปิด View > Code ดูเอง
		 *
		 * @param {Object} field ตัว `$v` ของช่องนั้น
		 * @returns {String|null}
		 */
		contentErrors(field) {
			if (!field.required) {
				return this.$t('global.error.required');
			}

			if (!field.jsonLdScriptOnly) {
				return 'ใน content มี <script> ที่ไม่ใช่ application/ld+json หรือ JSON-LD ที่วางไว้ไม่ถูกต้อง — เปิด View > Code เพื่อแก้';
			}

			return null;
		},
		handleSelectedLevel(level) {
			// Should clear parent id value
			if (level === PAGE_LEVEL.ONE) {
				this.pageParentLevelOne = null;
			}
		},
		handleUpdateStatus(value) {
			this.status = value;

			// When set status to false, should set visibility to false too
			if (value === false) {
				this.handleUpdateVisibility(false);
			}
		},
		handleUpdateVisibility(value) {
			this.visibility = value;
		},
		handleUpdateHeaderVisibility(value) {
			this.headerVisibility = value;
		},
		handleUpdateSidebarVisibility(value) {
			this.sidebarVisibility = value;

			// When the sidebar is shown, content must always be wrapped in a container
			if (value === true) {
				this.container = true;
			}
		},
		handleUpdateContainer(value) {
			this.container = value;
		},
		handleTogglePaddingSide(side, removed) {
			this.containerPadding = { ...this.containerPadding, [side]: !removed };
		},
		handleSubmit() {
			this.$v.$touch();

			if (this.$v.$invalid) {
				scrollToTop();
				return;
			}

			const params = {
				parent_id: this.isLevelOne ? null : this.pageParentLevelOne.value,
				name_en: this.name,
				name_th: this.nameTH,
				custom_link: this.customUrl ? this.customUrl : null, // if is empty string change it to null
				slug: this.slug,
				is_active: this.status,
				is_visible: this.visibility,
				is_header_visible: this.headerVisibility,
				is_sidebar_visible: this.sidebarVisibility,
				is_container: this.container,
				container_padding: this.containerPadding,
				content_en: this.content || undefined,
				content_th: this.contentTH || undefined,
				seo: {
					title_en: this.metaTitle,
					title_th: this.metaTitleTH,
					description_en: this.metaDescription,
					description_th: this.metaDescriptionTH,
					keyword_en: this.metaKeyword,
					keyword_th: this.metaKeywordTH,
					image: this.metaImage
						? this.metaImage.id
						: null,
				},
			};

			this.$emit('onSubmit', params);
		},
		handleAutoFillMetaTitleAndSlug() {
			if (!this.metaTitle) {
				this.metaTitle = this.name;
			}

			if (!this.slug) {
				this.slug = slugify(this.name, {
					lower: true,
					strict: true,
				});
				this.$v.slug.$touch();
			}
		},
		handleAutoFillMetaTitleTH() {
			if (!this.metaTitleTH) {
				this.metaTitleTH = this.nameTH;
			}
		},
		handleClearErrorSlug() {
			this.$emit('onClearErrorSlug');
		},
		handleRemove() {
			this.$emit('onRemove');
		},
		handlePreview() {
			const previewUrl = `${process.env.VUE_APP_ECOM_ENDPOINT}/th/mock/content?slug=${this.slug}`;
			window.open(previewUrl, '_blank');
		},
		getVisibilityText(value) {
			const text = mappingVisibility[value] || '';
			return this.$t(text);
		},
	},
};
</script>

<style lang="scss" scoped>
	hr {
		margin: rem(40) 0;
	}

	.level-label {
		margin-bottom: rem(4);
	}

	.footer-column-label {
		margin: rem(16) 0 rem(4) 0;
	}

	.toggle-label {
		width: rem(150);
	}

	.row-parent-page {
		// margin left, right come from .row
		margin-top: rem(16);
		margin-bottom: rem(-40);
	}

	.toggle-group {
		& + .toggle-group {
			margin-top: rem(32);
		}
	}

	.navigation-panel {
		position: fixed;
		left: rem(56);
		right: 0;
		bottom: 0;
		padding: rem(12) 0;
		box-shadow: 0 1px 2px 0 $color-gray-07, 0 4px 16px 0 $color-gray-15;
		background-color: $color-white;
		z-index: 9;

		@media (max-width: 991.98px) {
			left: 0;
		}

		::v-deep button {
			min-width: rem(80);
		}
	}
</style>
